import { useCallback, useEffect, useState } from 'react';

/**
 * Per-browser COP map layer preferences (what is drawn, clustering, labels).
 *
 * Mirrors the Pathfinder "Map Layer Controls" idea: one place to turn layers
 * and their labels on/off instead of a toolbar button per layer. Overlays
 * that are already toolbar toggles (traffic, radar, cameras, ...) keep their
 * existing state in COPMapGL; this covers the always-on layers that had no
 * switch before.
 */
export type CopMapLayerPrefs = {
  /** Device pins. */
  devices: boolean;
  /** Entity label drawn inside each pin. */
  deviceLabels: boolean;
  /** Group nearby device pins into count bubbles until zoomed in. */
  clusterDevices: boolean;
  /** Drone camera footprint wedges (needs devices). */
  cameraFootprint: boolean;
  /** Accumulated area a drone camera has observed (needs devices). */
  areaObserved: boolean;
  /** Alert markers: DFP, watchlist, co-traveler, mission threats. */
  alerts: boolean;
  /** Dwell / stop circles (fill + outline). */
  dwellCircles: boolean;
  /** Dwell center dot + duration text. */
  dwellLabels: boolean;
  /** Geofence polygons. */
  geofences: boolean;
  /** Geofence name text. */
  geofenceLabels: boolean;
  /** Itinerary stop numbers + component labels. */
  itineraryLabels: boolean;
};

export const DEFAULT_COP_MAP_LAYER_PREFS: CopMapLayerPrefs = {
  devices: true,
  deviceLabels: true,
  clusterDevices: false,
  cameraFootprint: true,
  areaObserved: true,
  alerts: true,
  dwellCircles: true,
  dwellLabels: true,
  geofences: true,
  geofenceLabels: true,
  itineraryLabels: true,
};

const STORAGE_KEY = 'cop.mapLayers.v1';

function load(): CopMapLayerPrefs {
  if (typeof window === 'undefined') return DEFAULT_COP_MAP_LAYER_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COP_MAP_LAYER_PREFS;
    const parsed = JSON.parse(raw) as Partial<CopMapLayerPrefs>;
    const out = { ...DEFAULT_COP_MAP_LAYER_PREFS };
    (Object.keys(out) as Array<keyof CopMapLayerPrefs>).forEach((k) => {
      if (typeof parsed[k] === 'boolean') out[k] = parsed[k] as boolean;
    });
    return out;
  } catch {
    return DEFAULT_COP_MAP_LAYER_PREFS;
  }
}

export function useCopMapLayerPrefs() {
  const [prefs, setPrefs] = useState<CopMapLayerPrefs>(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs]);

  const update = useCallback((patch: Partial<CopMapLayerPrefs>) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULT_COP_MAP_LAYER_PREFS), []);

  const isDefault = (Object.keys(DEFAULT_COP_MAP_LAYER_PREFS) as Array<keyof CopMapLayerPrefs>).every(
    (k) => prefs[k] === DEFAULT_COP_MAP_LAYER_PREFS[k]
  );

  return { prefs, update, reset, isDefault };
}
